/**
 * leadTime.js — Action Center "Lead Time" helpers.
 *
 * Backend supplies `event_at` (ISO 8601 UTC) on every action — see
 * `backend/services/action_engine.py::_event_at`. The UI computes
 * `(event_at - now) / 3600s` and renders it next to each action so the user
 * can see how much runway they have before the storm/promo/etc actually hits.
 *
 * Updates every 10 minutes via `useNow` so the displayed values track the
 * wall clock without re-fetching the action list (the underlying data is
 * cached for 30 min on the backend; recomputing the time-to-event is purely
 * client-side and free).
 */

/**
 * Parse an ISO 8601 timestamp into ms-since-epoch. Returns null for falsy
 * or unparseable input so callers can branch on null without try/catch.
 */
export function parseEventAt(eventAt) {
  if (!eventAt) return null
  // Accept Date objects too in case a caller pre-parses.
  if (eventAt instanceof Date) {
    const t = eventAt.getTime()
    return Number.isFinite(t) ? t : null
  }
  const t = Date.parse(String(eventAt))
  return Number.isFinite(t) ? t : null
}

/**
 * Hours from `nowMs` until `eventAt`. Negative if the event is in the past.
 * Returns null if eventAt is missing or unparseable.
 */
export function leadTimeHours(eventAt, nowMs = Date.now()) {
  const t = parseEventAt(eventAt)
  if (t === null) return null
  return (t - nowMs) / 3_600_000
}

/**
 * Render a lead time number as a short, human-friendly string.
 *   null    → '—'           (no event_at available)
 *   < 0     → 'Past'        (event already happened)
 *   < 1     → '<1h'         (imminent)
 *   < 100   → 'Nh'          (still expressed in hours per the spec)
 *   >= 100  → 'Nh (Md)'     (also call out days for very-far events,
 *                            because "192h" is hard to eyeball)
 */
export function formatLeadTime(hours) {
  if (hours == null) return '—'
  if (hours < 0) return 'Past'
  if (hours < 1) return '<1h'
  const h = Math.round(hours)
  if (h < 100) return `${h}h`
  const d = Math.round(hours / 24)
  return `${h}h (${d}d)`
}

/**
 * Return a tier name for styling: 'now' | 'soon' | 'planned' | 'far' | 'past'.
 * Used by the Action Center to colour-code the lead-time badge.
 */
export function leadTimeTier(hours) {
  if (hours == null) return 'unknown'
  if (hours < 0) return 'past'
  if (hours < 24) return 'now'
  if (hours < 72) return 'soon'
  if (hours < 168) return 'planned'
  return 'far'
}
